import { navigate, RouteComponentProps } from "@reach/router";
import { gql } from "graphql-tag";
import React, { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import logger from "services/logger";
import { LoadingSpinner, ServerError } from "components/graphql-views";
import styles from "../adminDashboard.module.css";
import OrganizationForm, { formSchema, OrgForm } from "./form";

const ORGANIZATION = gql`
  query getOrg($id: ID!) {
    organization(id: $id) {
      id
      name
      type
      email
      bccEmail
      phone
      address1
      address2
      city
      state
      zip
      invoicedCustomer {
        invoicedId
        active
      }
    }
  }
`;

const UPDATE_ORGANIZATION = gql`
  mutation updateOrg($id: ID!, $data: OrganizationInput!) {
    updateOrganization(id: $id, data: $data) {
      id
      name
      type
      email
      bccEmail
      phone
      address1
      address2
      city
      state
      zip
      invoicedCustomer {
        invoicedId
        active
      }
    }
  }
`;

interface QueryData {
  organization: {
    id: string;
    type: string;
    name: string;
    phone: string;
    phoneExt: string;
    email: string;
    bccEmail: string;
    address1: string;
    address2?: string;
    city: string;
    state: string;
    zip: string;
  };
}

interface Props {
  id?: string;
  path: string;
}

interface ResData {
  organization: {
    id: string;
  };
}

const UpdateOrganization: React.FC<RouteComponentProps<Props>> = ({ id: orgId }) => {
  const [sending, setSending] = useState(false);
  const { loading, error, data } = useQuery<QueryData>(ORGANIZATION, { variables: { id: orgId } });
  const [updateOrg] = useMutation<ResData>(UPDATE_ORGANIZATION);

  if (error) return <ServerError desc={error.message} />;
  if (loading) return <LoadingSpinner />;
  if (!data) return null;

  const submitForm = (formData: OrgForm) => {
    setSending(true);
    updateOrg({ variables: { id: orgId, data: formData } })
      .then(() => navigate("/dash/admin/organizations"))
      .catch((err) => {
        logger.sendError(err);
        setSending(false);
      });
  };

  // casting strips __typename from the apollo response data
  // later this will also take null data and make it an empty string
  // defaulting type to property
  const organization = formSchema.cast({ ...data.organization, type: "property" });

  return (
    <div>
      <h3 className={`${styles.heading}`}>Update an Organization</h3>
      <OrganizationForm
        formType="update"
        // @ts-ignore
        initialData={organization}
        sending={sending}
        handleSubmit={submitForm}
      />
    </div>);
};

export default UpdateOrganization;
